/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatNumber } from '../../../utils/numberFormat';
import { getProgressTypeText, supportsProgressTypeDisplay } from '../../../utils/progressTypeText';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import {
  BAND_OPACITY,
  PREVIEW_HEIGHT,
  PREVIEW_WIDTH,
  buildPreviewBandRegions,
} from '../../ragProfiles/ragProfilesPreview';
import {
  BandKey,
  RagBand,
  buildBandDefinitionRows,
  definitionsNote,
  directionTargetText,
  directionText,
  isGuardrailCounting,
  progressColorToBandKey,
  readableTextColor,
} from './kr.hover.ragBandDefinitions';

interface RagPopupContext {
  bands: RagBand[];
  bandLabels: Record<BandKey, string>;
  bandColors: Record<BandKey, string>;
}

export interface KrHoverNewRagData {
  progress_color_raw: string | number;
  achieveTarget: string | number;
  expectedValue: number | null;
  expectedProgress: number | null;
  initialData: string | number;
  targetValue: string | number;
  metricDaysLeft: number;
  metricProgress: number;
  metric?: string | number;
  source?: string | number;
  mirrorSource?: string | number;
  progress_type?: string | number | null;
  milestones?: unknown[];
  daysOverPer?: number;
  displayDateText?: string;
  mitIntervalCount?: number;
  calcStart?: number | null;
  calcTarget?: number | null;
  calcActual?: number | null;
  ragScore?: number | null;
  neverUpdated?: boolean | null;
  counting: string;
  committed_target?: string | number;
  ragPopupContext: RagPopupContext;
}

interface KrHoverNewRagProps {
  data: KrHoverNewRagData;
}

const Container = styled.div({
  backgroundColor: '#FFFFFF',
  borderRadius: '10px',
  border: '1px solid #e2e4e8',
  boxShadow: '0 10px 30px rgba(20, 30, 50, 0.14)',
  overflow: 'hidden',
  textAlign: 'left',
});

const Header = styled.div({
  padding: '9px 22px',
  fontSize: '18px',
  fontWeight: 700,
  letterSpacing: '-0.3px',
  lineHeight: 1.25,
});

const Body = styled.div({
  margin: '18px 22px',
});

const InputRow = styled.div({
  fontSize: '15px',
  color: '#2b2b2b',
  lineHeight: 1.5,
  margin: '6px 0',
  '& strong': {
    color: '#111',
  },
});

const SectionTitle = styled.div({
  fontSize: '11px',
  fontWeight: 700,
  color: '#2589A3',
  textTransform: 'uppercase',
  letterSpacing: '.09em',
  borderTop: '1px solid #e3e7ea',
  margin: '18px 0 4px',
  paddingTop: '14px',
});

const NeverUpdated = styled.em({
  color: '#7a8792',
});

const ChartBlock = styled.div({
  padding: '0 22px 20px',
});

const Chart = styled.svg({
  display: 'block',
  width: '100%',
  height: 'auto',
});

const Footer = styled.div({
  borderTop: '1px solid #F0F0F0',
  padding: '12px 22px 16px',
  position: 'relative',
});

const DefinitionsLink = styled.button({
  background: 'none',
  border: 'none',
  borderBottom: '1px dotted #8a8a8a',
  padding: 0,
  fontSize: '12.5px',
  color: '#8a8a8a',
  cursor: 'help',
});

const DefinitionsPopover = styled.div({
  position: 'absolute',
  left: '22px',
  width: '330px',
  bottom: '100%',
  boxSizing: 'border-box',
  backgroundColor: '#FFFFFF',
  border: '1px solid #e2e4e8',
  borderRadius: '8px',
  boxShadow: '0 10px 28px rgba(20, 30, 50, 0.2)',
  padding: '12px 14px',
  zIndex: 2,
});

const DefinitionRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '9px',
  fontSize: '13px',
  lineHeight: '20px',
  color: '#2b2b2b',
  marginBottom: '8px',
  '&:last-child': {
    marginBottom: 0,
  },
});

const Dot = styled.span({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  flexShrink: 0,
});

const DefinitionsNote = styled.div({
  fontSize: '11.5px',
  color: '#8a8a8a',
  marginTop: '10px',
  paddingTop: '10px',
  borderTop: '1px solid #F0F0F0',
});

const isFalsey = (value: number | string | null | undefined) =>
  value === null || value === undefined || value === '';

const round = (value: number | string) => {
  if (typeof value === 'string' && /[KMBT]$/i.test(value.trim())) {
    return value;
  }
  return Number(value).toFixed(0);
};

const numberToUnit = (value: number | string | null | undefined) => {
  if (isFalsey(value)) {
    return '--';
  }
  return formatNumber(value);
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const W = PREVIEW_WIDTH;
const H = PREVIEW_HEIGHT;
const CHART_LINE = '#7fd6a2';
const MARKER = '#1c3350';
const MIT_SOURCES = ['12', '13', '14'];

const isNum = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

interface CalcChartProps {
  data: KrHoverNewRagData;
  expectedProgress: number | null;
  gap: number | null;
  breached: boolean;
  limitLabel: string;
  todayLabel: string;
}

function CalcChart({
  data, expectedProgress, gap, breached, limitLabel, todayLabel,
}: CalcChartProps) {
  const { bands, bandColors } = data.ragPopupContext;
  const counting = String(data.counting);
  // On the Expected line x and y share the progress scale, so the dot never needs clamping.
  const markerProgress = expectedProgress ?? clamp(Number(data.daysOverPer ?? 0), 0, 100);
  const markerX = (W * markerProgress) / 100;
  let plot: React.ReactNode;
  let dotY: number;

  if (isGuardrailCounting(counting)) {
    const limitY = H / 3;
    const mustStayBelow = counting === '8';
    const { calcActual, calcTarget } = data;
    if (isNum(calcActual) && isNum(calcTarget) && calcTarget > 0) {
      // Value scale 0..1.5 × limit, so the limit sits a third of the way down.
      dotY = clamp(H * (1 - calcActual / calcTarget / 1.5), 0, H);
    } else {
      dotY = breached === mustStayBelow ? limitY / 2 : (limitY + H) / 2;
    }
    plot = (
      <>
        <rect
          x={0}
          y={0}
          width={W}
          height={limitY}
          fill={bandColors[mustStayBelow ? 'at_risk' : 'above_plan']}
          fillOpacity={BAND_OPACITY[mustStayBelow ? 'at_risk' : 'above_plan']}
        />
        <rect
          x={0}
          y={limitY}
          width={W}
          height={H - limitY}
          fill={bandColors[mustStayBelow ? 'above_plan' : 'at_risk']}
          fillOpacity={BAND_OPACITY[mustStayBelow ? 'above_plan' : 'at_risk']}
        />
        <line x1={0} y1={limitY} x2={W} y2={limitY} stroke={CHART_LINE} strokeWidth={2} strokeDasharray="5 4" />
        <text x={6} y={limitY - 8} fontSize={10} fill="#4f7a5f">{limitLabel}</text>
      </>
    );
  } else {
    const dotProgress = gap === null ? data.metricProgress : markerProgress + gap;
    dotY = H * (1 - clamp(dotProgress, 0, 100) / 100);
    if (counting === '2') {
      dotY = H - dotY;
    }
    plot = (
      <g transform={counting === '2' ? `matrix(1 0 0 -1 0 ${H})` : undefined}>
        {buildPreviewBandRegions(bands, bandColors).map((region) => (
          <polygon
            key={region.key}
            points={region.points}
            fill={region.color}
            fillOpacity={region.opacity}
          />
        ))}
        <line x1={0} y1={H} x2={W} y2={0} stroke={CHART_LINE} strokeWidth={2} strokeDasharray="5 4" />
      </g>
    );
  }

  return (
    <Chart viewBox={`-8 -8 ${W + 16} ${H + 34}`} aria-hidden="true" data-testid="kr-new-rag-chart">
      <svg x={0} y={0} width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        {plot}
      </svg>
      <line x1={markerX} y1={0} x2={markerX} y2={H} stroke="#9aa3ad" strokeWidth={1} strokeDasharray="3 3" />
      <circle cx={markerX} cy={dotY} r={5} fill="#FFFFFF" stroke={MARKER} strokeWidth={3} />
      <text x={clamp(markerX, 20, W - 20)} y={H + 20} fontSize={10} fill="#8a95a0" textAnchor="middle">
        {todayLabel}
      </text>
    </Chart>
  );
}

export default function KrHoverNewRag({ data }: KrHoverNewRagProps) {
  const { t } = useTranslation();
  const [showDefinitions, setShowDefinitions] = useState(false);
  const [definitionsRef] = useOnClickOutside(() => setShowDefinitions(false), showDefinitions);

  useEffect(() => {
    if (!showDefinitions) return undefined;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Capture + stop so the first Escape only closes the box, not the whole popup.
        e.stopImmediatePropagation();
        setShowDefinitions(false);
      }
    };
    document.addEventListener('keydown', onEscape, true);
    return () => document.removeEventListener('keydown', onEscape, true);
  }, [showDefinitions]);

  const {
    progress_color_raw,
    achieveTarget,
    initialData,
    targetValue,
    metricDaysLeft,
    metricProgress,
    counting,
    committed_target,
    mitIntervalCount,
    ragPopupContext,
    calcStart,
    calcTarget,
    ragScore,
  } = data;

  const isCommitted = String(committed_target) === '1';
  const guardrail = isGuardrailCounting(counting);
  const neverUpdated = data.neverUpdated ?? data.displayDateText === 'Never updated';
  const straightLine = !data.milestones?.length && !MIT_SOURCES.includes(String(data.source));
  const scored = data.expectedProgress != null;
  // Unscored straight-line KRs fall back to elapsed time; MIT and milestone plans aren't linear.
  const expectedProgress = scored || !straightLine
    ? data.expectedProgress
    : Math.round(clamp(Number(data.daysOverPer ?? 0), 0, 100));
  let gap: number | null = null;
  if (scored && isNum(ragScore)) {
    gap = Math.round(ragScore);
  } else if (expectedProgress != null) {
    gap = Math.round(metricProgress - expectedProgress);
  }
  let expectedValue = scored ? data.expectedValue : null;
  if (!scored && expectedProgress != null && isNum(calcStart) && isNum(calcTarget)) {
    expectedValue = calcStart + ((calcTarget - calcStart) * expectedProgress) / 100;
  }

  const bandKey = progressColorToBandKey(progress_color_raw);
  const headerColor = ragPopupContext.bandColors[bandKey] ?? '#666';
  const bandLabel = ragPopupContext.bandLabels[bandKey] ?? '';
  const isPercent = String(data.metric) === '3';
  const show = (value: number | string | null | undefined) => {
    const text = numberToUnit(value);
    return isPercent && text !== '--' && !text.endsWith('%') ? `${text}%` : text;
  };
  const target = show(targetValue);
  const breached = bandKey !== 'above_plan';
  const definitionRows = buildBandDefinitionRows(
    ragPopupContext.bands,
    ragPopupContext.bandLabels,
    ragPopupContext.bandColors,
    t,
  );

  let gapRow: React.ReactNode = null;
  if (guardrail && gap !== null) {
    gapRow = (
      <>
        <span> · {t('Points behind')} </span>
        <strong>{Math.max(0, -gap)}%</strong>
      </>
    );
  } else if (gap === 0) {
    gapRow = <span> · {t('on plan')}</span>;
  } else if (gap !== null) {
    gapRow = (
      <>
        <span> · {gap > 0 ? t('Points ahead') : t('Points behind')} </span>
        <strong>{Math.abs(gap)}%</strong>
      </>
    );
  }

  let intervals: string | null = null;
  if (mitIntervalCount) {
    intervals = mitIntervalCount === 1
      ? t('across 1 interval')
      : t('across {{count}} intervals', { count: mitIntervalCount });
  }

  const neverUpdatedMark = neverUpdated && (
    <span>
      {' · '}
      <NeverUpdated>{t('Never Updated')}</NeverUpdated>
    </span>
  );
  const showCalculation = supportsProgressTypeDisplay(data);

  return (
    <Container className="kr-hover-new-rag" data-testid="kr-hover-new-rag">
      <Header
        style={{ backgroundColor: headerColor, color: readableTextColor(headerColor) }}
        data-testid="kr-new-rag-header"
      >
        {bandLabel}
      </Header>

      <Body>
        <InputRow>
          <span>{t('Current Value')} </span>
          <strong>{show(achieveTarget === '--' ? initialData : achieveTarget)}</strong>
          {guardrail && (
            <>
              <span> · {t('Limit')} </span>
              <strong>{target}</strong>
              {neverUpdatedMark}
            </>
          )}
        </InputRow>
        {!guardrail && (
          <InputRow>
            <span>{t('Where you should be by now')} </span>
            <strong>{show(expectedValue)}</strong>
            {neverUpdatedMark}
          </InputRow>
        )}
        <InputRow>
          <span>{t('Final Target')} </span>
          <strong>{target}</strong>
          {guardrail ? (
            <span> · {t('Full quarter')} · {breached ? t('Limit breached') : t('On track')}</span>
          ) : (
            <span> · {metricDaysLeft} {t('days left')}</span>
          )}
        </InputRow>

        <SectionTitle>{t('Key Result Overview')}</SectionTitle>
        {guardrail ? (
          <InputRow>
            <span>{String(counting) === '8' ? t('Must stay at or below') : t('Must stay at or above')} </span>
            <strong>{target}</strong>
            <span> {t('for the full quarter')}</span>
          </InputRow>
        ) : (
          <InputRow>
            <span>{t('Starting from')} </span>
            <strong>{show(initialData)}</strong>
            <span>, {directionText(counting, t)} </span>
            <strong>{directionTargetText(counting, target, t)}</strong>
            {intervals && <span> {intervals}</span>}
          </InputRow>
        )}
        {showCalculation && (
          <InputRow>
            <span>{t('Calculation:', { nsSeparator: false })} </span>
            <strong>
              {getProgressTypeText(data.progress_type as string | number, data.mirrorSource || data.source || '', counting, t)}
            </strong>
          </InputRow>
        )}

        <SectionTitle>{t('Translation to % Points')}</SectionTitle>
        <InputRow>
          <span>{t('Current progress')} </span>
          <strong>{metricProgress ? round(metricProgress) : 0}%</strong>
        </InputRow>
        <InputRow>
          <span>{t('Expected progress to date')} </span>
          <strong>{expectedProgress != null ? `${round(expectedProgress)}%` : '--'}</strong>
          {gapRow}
        </InputRow>
      </Body>

      <ChartBlock>
        <CalcChart
          data={data}
          expectedProgress={expectedProgress}
          gap={gap}
          breached={breached}
          limitLabel={t('limit {{value}}', { value: target })}
          todayLabel={t('today')}
        />
      </ChartBlock>

      <Footer
        ref={definitionsRef}
        onMouseEnter={() => setShowDefinitions(true)}
        onMouseLeave={() => setShowDefinitions(false)}
      >
        {showDefinitions && (
          <DefinitionsPopover data-testid="kr-rag-band-definitions">
            {definitionRows.map((row) => (
              <DefinitionRow key={row.key}>
                <Dot style={{ backgroundColor: row.color }} aria-hidden="true" />
                <span>
                  <strong>{row.label}</strong> — {row.description}
                </span>
              </DefinitionRow>
            ))}
            <DefinitionsNote>
              {definitionsNote(isCommitted, definitionRows.length, ragPopupContext.bandLabels, t)}
            </DefinitionsNote>
          </DefinitionsPopover>
        )}
        <DefinitionsLink
          type="button"
          data-testid="kr-rag-band-definitions-link"
          aria-expanded={showDefinitions}
          onClick={() => setShowDefinitions(true)}
          onFocus={() => setShowDefinitions(true)}
          onBlur={() => setShowDefinitions(false)}
        >
          {isCommitted ? t('Committed RAG band definitions') : t('RAG band definitions')}
        </DefinitionsLink>
      </Footer>
    </Container>
  );
}
